import React, { useState, useEffect } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useScrollLock } from '../hooks/useScrollLock';
import { useSheetDrag } from '../hooks/useSheetDrag';
import { useExerciseAliasIds } from '../hooks/useExerciseAliasIds';
import { filterExerciseTemplates, normalizeExerciseName } from '../lib/exerciseSearch';
import { getExerciseTemplates, getLastPerformedByTemplate } from '../lib/database';
import { isTimeType, usesWeight as usesWeightHelper } from '../lib/exerciseType';
import ProposeExerciseSheet from './ProposeExerciseSheet';

/**
 * Bottom sheet CONDIVISO per aggiungere un esercizio (scheda: "esercizio extra",
 * e allenamento libero). Un solo componente per evitare divergenze/bug futuri.
 *
 * Caratteristiche:
 * - layout che si ridimensiona tra banner recupero e tastiera (campo nome e
 *   pulsanti sempre visibili, parametri in area scrollabile);
 * - ricerca alias-aware con dropdown leggibile;
 * - ultimo dato usato per l'esercizio selezionato (visibile + pre-compilazione);
 * - step di conferma prima dell'inserimento;
 * - rispetto di exercise_type / uses_weight (niente kg se non previsto; tempo/
 *   durata per time/hold/cardio).
 *
 * onAdd riceve un OGGETTO:
 *   { name, sets, target, weight, templateId, restSeconds, exerciseType, usesWeight }
 * dove `target` = reps | secondi (time/hold) | minuti (cardio).
 */
function AddExerciseSheet({
  onClose,
  onAdd,
  profile,
  isRestTimerActive = false,
  title = 'Aggiungi Esercizio',
  defaultSets = '3',
  defaultRestSeconds = ''
}) {
  useScrollLock(true);
  const toast = useToast();
  const { contentRef, sheetStyle, overlayOpacity, handleTouchStart: sheetTouchStart, handleTouchMove: sheetTouchMove, handleTouchEnd: sheetTouchEnd, handleClose } = useSheetDrag(onClose);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [exerciseTemplates, setExerciseTemplates] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sets, setSets] = useState(defaultSets);
  const [target, setTarget] = useState('10'); // reps | secondi (time/hold) | minuti (cardio)
  const [weight, setWeight] = useState('');
  const [restSeconds, setRestSeconds] = useState(defaultRestSeconds);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [viewportH, setViewportH] = useState(() =>
    (typeof window !== 'undefined' && window.visualViewport?.height) || (typeof window !== 'undefined' ? window.innerHeight : 800)
  );
  const [showProposeSheet, setShowProposeSheet] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [lastData, setLastData] = useState(null);
  const [lastLoading, setLastLoading] = useState(false);

  // Tipo esercizio selezionato → guida i campi mostrati (rispetta il catalogo pulito)
  const exType = selectedTemplate?.exerciseType || 'reps';
  const isTime = isTimeType(exType); // 'time' | 'hold'
  const isCardio = exType === 'cardio' || exType === 'distance';
  const showWeight = usesWeightHelper({ exercise_type: exType, uses_weight: selectedTemplate?.usesWeight });
  const targetLabel = isTime ? 'Tempo (sec)' : isCardio ? 'Durata (min)' : 'Reps';

  // iOS Keyboard handling via visualViewport (traccia anche l'altezza visibile)
  useEffect(() => {
    const handleResize = () => {
      if (window.visualViewport) {
        const viewportHeight = window.visualViewport.height;
        const windowHeight = window.innerHeight;
        const kbHeight = windowHeight - viewportHeight;
        setKeyboardHeight(kbHeight > 50 ? kbHeight : 0);
        setViewportH(viewportHeight);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      }
    };
  }, []);

  useEffect(() => {
    loadExerciseTemplates();
  }, []);

  // Carica l'ultimo dato usato quando si seleziona un esercizio del catalogo
  useEffect(() => {
    if (!selectedTemplate?.id || !profile?.id) {
      setLastData(null);
      return;
    }
    let cancelled = false;
    setLastLoading(true);
    getLastPerformedByTemplate(profile.id, selectedTemplate.id)
      .then(d => {
        if (cancelled) return;
        setLastData(d);
        // Pre-compila per comodità (solo se l'utente non ha già digitato)
        if (d) {
          if (showWeight && d.weight != null) setWeight(prev => prev || String(d.weight));
          if (!isTime && !isCardio && d.reps != null) setTarget(prev => (prev === '10' ? String(d.reps) : prev));
        }
      })
      .finally(() => { if (!cancelled) setLastLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id, profile?.id]);

  const loadExerciseTemplates = async () => {
    try {
      const templates = await getExerciseTemplates();
      setExerciseTemplates(templates);
    } catch (error) {
      console.error('Error loading exercise templates:', error);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setShowSuggestions(query.length > 0);
    setSelectedTemplate(null);
  };

  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setSearchQuery(template.name);
    setShowSuggestions(false);
    // Default sensato per il tipo
    const t = template?.exerciseType || 'reps';
    if (isTimeType(t)) setTarget('30');
    else if (t === 'cardio' || t === 'distance') setTarget('10');
    else setTarget('10');
    // Azzera il peso: verrà pre-compilato dall'ultimo dato se previsto
    setWeight('');
  };

  // Match locale immediato (nome + token) piu' gli alias risolti dalla RPC:
  // "RDL" o "lat machine" trovano l'esercizio anche qui, non solo nel catalogo.
  const aliasIds = useExerciseAliasIds(searchQuery);
  const filteredTemplates = filterExerciseTemplates(exerciseTemplates, searchQuery, aliasIds);

  const resolveTemplateId = () => {
    let templateId = selectedTemplate?.id || null;
    if (!templateId && searchQuery.trim()) {
      // Confronto normalizzato (come il DB): accenti e spazi di troppo non
      // devono far perdere il collegamento al template, che regge storico e
      // ultimo dato.
      const typed = normalizeExerciseName(searchQuery);
      const matchingTemplate = exerciseTemplates.find(
        t => normalizeExerciseName(t.name) === typed
      );
      if (matchingTemplate) templateId = matchingTemplate.id;
    }
    return templateId;
  };

  const exerciseName = selectedTemplate?.name || searchQuery.trim();

  // Step 1: valida e mostra la conferma
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!exerciseName) {
      toast.warning('Inserisci il nome dell\'esercizio');
      return;
    }
    if (!sets || !target) {
      toast.warning(`Inserisci serie e ${targetLabel.toLowerCase()}`);
      return;
    }
    // Chiudi la tastiera prima di mostrare il riepilogo (più spazio, no input attivi)
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
    setConfirming(true);
  };

  // Step 2: conferma → inserisci davvero
  const handleConfirmAdd = () => {
    onAdd({
      name: exerciseName,
      sets,
      target,
      weight: showWeight ? weight : '',
      templateId: resolveTemplateId(),
      restSeconds,
      exerciseType: exType,
      usesWeight: showWeight
    });
    setConfirming(false);
    setSelectedTemplate(null);
    setSearchQuery('');
    setSets(defaultSets);
    setTarget('10');
    setWeight('');
    setRestSeconds(defaultRestSeconds);
  };

  return (
    <div
      className="fixed inset-0 z-50 animate-fade-in"
      style={{ backgroundColor: `rgba(0,0,0,${overlayOpacity})` }}
      onClick={handleClose}
    >
      <div
        ref={contentRef}
        className="absolute left-0 right-0 bg-white rounded-t-2xl shadow-2xl flex flex-col"
        style={{
          ...sheetStyle,
          // Altezza vincolata allo spazio visibile tra banner recupero e tastiera:
          // il campo nome e i pulsanti restano sempre a schermo.
          maxHeight: `${Math.max(240, viewportH - (isRestTimerActive ? 84 : 0) - 8)}px`,
          bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0',
          transition: keyboardHeight > 0 ? 'bottom 0.25s ease-out' : sheetStyle.transition
        }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={sheetTouchStart}
        onTouchMove={sheetTouchMove}
        onTouchEnd={sheetTouchEnd}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 flex-none">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="flex items-center justify-between px-6 pb-3 flex-none">
          <h2 className="text-xl font-bold text-gray-900">
            {confirming ? 'Conferma esercizio' : title}
          </h2>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full text-gray-600 transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {confirming ? (
          /* ---------- STEP 2: RIEPILOGO + CONFERMA ---------- */
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-2">
              <p className="text-sm text-gray-600 mb-4">Vuoi aggiungere questo esercizio?</p>
              <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                <ConfirmRow label="Esercizio" value={exerciseName} />
                <ConfirmRow label="Serie" value={sets} />
                <ConfirmRow label={targetLabel} value={`${target}${isTime ? ' sec' : isCardio ? ' min' : ''}`} />
                {showWeight && (
                  <ConfirmRow label="Kg" value={weight ? `${weight} kg` : 'Corpo libero'} />
                )}
                <ConfirmRow label="Recupero" value={restSeconds ? `${restSeconds}s` : 'Nessuno'} />
              </div>
            </div>
            <div className="flex gap-3 px-6 pt-3 pb-6 flex-none border-t border-gray-100" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))' }}>
              <button
                type="button"
                onClick={() => setConfirming(false)}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                Indietro
              </button>
              <button
                type="button"
                onClick={handleConfirmAdd}
                className="flex-1 py-3 px-4 gradient-primary text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all active:scale-98"
              >
                Conferma
              </button>
            </div>
          </div>
        ) : (
          /* ---------- STEP 1: FORM ---------- */
          <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
            {/* Campo nome: SEMPRE visibile (fuori dall'area scrollabile) */}
            <div className="relative px-6 flex-none">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Nome Esercizio
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={() => setShowSuggestions(searchQuery.length > 0)}
                placeholder="Cerca esercizio..."
                required
                autoFocus
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />

              {showSuggestions && filteredTemplates.length > 0 && (
                <div className="absolute z-10 left-6 right-6 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {filteredTemplates.slice(0, 10).map(template => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => handleSelectTemplate(template)}
                      className="w-full text-left px-4 py-3 hover:bg-primary-50 transition-colors border-b border-gray-100 last:border-0"
                    >
                      <div className="font-semibold text-gray-900">{template.name}</div>
                      <div className="text-xs text-gray-600 mt-1">
                        {template.primaryMuscle}
                        {template.secondaryMuscles && template.secondaryMuscles.length > 0 &&
                          ` • ${template.secondaryMuscles.map(m => m.muscleGroup).join(', ')}`
                        }
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showSuggestions && searchQuery && filteredTemplates.length === 0 && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setShowProposeSheet(true)}
                    className="w-full px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg text-left text-sm text-amber-800 font-medium hover:bg-amber-100 transition-colors"
                  >
                    💡 Non trovi "{searchQuery}"? Proponilo allo staff →
                  </button>
                </div>
              )}

              {selectedTemplate && (
                <div className="mt-2 px-3 py-2 bg-success-50 text-success-700 rounded-lg text-sm font-semibold">
                  Selezionato: {selectedTemplate.name}
                  {selectedTemplate.isPending && <span className="ml-2 text-xs opacity-70">(proposta in attesa)</span>}
                </div>
              )}

              {/* Ultimo dato usato per l'esercizio selezionato */}
              {selectedTemplate && (
                <div className="mt-2 px-3 py-2 bg-blue-50 text-blue-800 rounded-lg text-xs">
                  {lastLoading
                    ? 'Ultimo utilizzo: …'
                    : formatLastUsed(lastData, { isTime, isCardio, showWeight })}
                </div>
              )}
            </div>

            {showProposeSheet && (
              <ProposeExerciseSheet
                initialName={searchQuery}
                userId={profile?.id}
                userRole={profile?.role}
                allowAddToSession={true}
                onClose={() => setShowProposeSheet(false)}
                onProposed={({ template }) => {
                  setShowProposeSheet(false);
                  setSelectedTemplate({ ...template, primaryMuscle: template.primary_muscle_group, exerciseType: template.exercise_type, usesWeight: template.uses_weight, isPending: true });
                  setSearchQuery(template.name);
                  setShowSuggestions(false);
                }}
              />
            )}

            {/* Area scrollabile: parametri (usa lo spazio tra nome e pulsanti) */}
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-3 space-y-4">
              <div className={`grid ${showWeight ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                <div className="min-w-0">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Serie</label>
                  <input
                    type="number"
                    value={sets}
                    onChange={(e) => setSets(e.target.value)}
                    min="1"
                    max="20"
                    required
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  />
                </div>

                <div className="min-w-0">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">{targetLabel}</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={target}
                    onChange={(e) => setTarget(e.target.value)}
                    placeholder={isTime ? '30' : isCardio ? '10' : '10'}
                    required
                    className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                  />
                </div>

                {showWeight && (
                  <div className="min-w-0">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Kg</label>
                    <input
                      type="number"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value)}
                      placeholder="Opt"
                      min="0"
                      step="0.5"
                      className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-base"
                    />
                  </div>
                )}
              </div>

              {/* Rest timer field */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Recupero (secondi) <span className="font-normal text-gray-400">opzionale</span>
                </label>
                <div className="flex gap-2">
                  {[60, 90, 120].map(sec => (
                    <button
                      key={sec}
                      type="button"
                      onClick={() => setRestSeconds(restSeconds === String(sec) ? '' : String(sec))}
                      className={`px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
                        restSeconds === String(sec)
                          ? 'bg-accent-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {sec}s
                    </button>
                  ))}
                  <input
                    type="number"
                    value={![60, 90, 120].includes(Number(restSeconds)) ? restSeconds : ''}
                    onChange={(e) => setRestSeconds(e.target.value)}
                    placeholder="Altro"
                    min="10"
                    max="600"
                    className="flex-1 min-w-0 px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Pulsanti: SEMPRE visibili (fuori dall'area scrollabile) */}
            <div className="flex gap-3 px-6 pt-3 pb-6 flex-none border-t border-gray-100" style={{ paddingBottom: 'max(24px, env(safe-area-inset-bottom, 24px))' }}>
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold transition-colors"
              >
                Annulla
              </button>
              <button
                type="submit"
                className="flex-1 py-3 px-4 gradient-primary text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all active:scale-98"
              >
                Aggiungi
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Riga del riepilogo di conferma
function ConfirmRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-semibold text-gray-900 text-right ml-3 break-words">{value}</span>
    </div>
  );
}

// Formatta il testo "ultimo dato usato" rispettando il tipo di esercizio
function formatLastUsed(lastData, { isTime, isCardio, showWeight }) {
  if (!lastData) return 'Primo utilizzo per questo esercizio';

  const { weight, reps, duration_minutes, distance, date } = lastData;
  const parts = [];

  if (isCardio) {
    if (duration_minutes != null) parts.push(`${duration_minutes} min`);
    if (distance != null) parts.push(`${distance} km`);
  } else if (isTime) {
    if (duration_minutes != null) parts.push(`${duration_minutes} min`);
    else if (reps != null) parts.push(`${reps} sec`);
  } else {
    if (reps != null) parts.push(`${reps} reps`);
    if (showWeight && weight != null) parts.push(`${weight} kg`);
  }

  // Fallback: se il tipo non ha prodotto nulla, mostra ciò che c'è
  if (parts.length === 0) {
    if (weight != null) parts.push(`${weight} kg`);
    if (reps != null) parts.push(`${reps} reps`);
    if (duration_minutes != null) parts.push(`${duration_minutes} min`);
    if (distance != null) parts.push(`${distance} km`);
  }

  if (parts.length === 0) return 'Nessun dato registrato';

  let dateStr = '';
  if (date) {
    try {
      dateStr = ` · ${new Date(date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}`;
    } catch (e) { /* ignore */ }
  }
  return `Ultimo: ${parts.join(' × ')}${dateStr}`;
}

export default AddExerciseSheet;
